import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { EFieldType, type TField } from '@timeedit/types';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Typography } from '@timeedit/ui-components/antd';
import {
  calculateProperties,
  mapCol2,
  preferencesFilterOptions,
} from './fieldUtils';

type PropertiesProps = {
  value?: TField;
  onChangeValue: (type: string, value: any, obj?: any) => void;
};

export const Properties = ({ value, onChangeValue }: PropertiesProps) => {
  const allObjectTypes = useSelector(selectObjectTypesList);
  const objectTypesOptions = useMemo(
    () =>
      allObjectTypes.map((type) => ({
        label: type.name,
        value: type.id,
      })),
    [allObjectTypes]
  );

  return (
    <TEDrawerSection label="Properties">
      <div className="wrapper-section">
        <div className="section-item">
          <TEInput.Select
            value={value.filter}
            onChange={(e) => onChangeValue('filter', e)}
            placeholder="Select"
            label="Filter"
            tooltip="Filter"
            disabled={
              !get(mapCol2, [value.fieldType, 'filter', 'isEnabled'], true)
            }
            options={preferencesFilterOptions}
            data-testid="property-filter"
          />
          <TEInput.InputNumber
            value={value.length}
            onChange={(e) => onChangeValue('length', e)}
            label="Length"
            tooltip="Length"
            disabled={
              !get(mapCol2, [value.fieldType, 'length', 'isEnabled'], true)
            }
            data-testid="property-length"
          />
          <TEInput.InputNumberMinMax
            debounceTime={0}
            value={[value.min, value.max]}
            onChange={(e) => {
              const [min, max] = e;
              onChangeValue('', null, { min, max });
            }}
            label="Input Labels"
            tooltip="Input Labels"
            disabled={
              !get(mapCol2, [value.fieldType, 'minMax', 'isEnabled'], true)
            }
            data-testid="property-input-label"
          />
          <TEInput.Select
            value={value.sumType || undefined}
            placeholder="Select"
            onChange={(e) => onChangeValue('sumType', e || 0)}
            label="Sum type"
            tooltip="Sum type"
            disabled={
              !get(mapCol2, [value.fieldType, 'sumType', 'isEnabled'], true)
            }
            options={objectTypesOptions}
            data-testid="property-sum-type"
          />
          <TEInput.Select
            value={value.topLevelType || undefined}
            placeholder="Select"
            onChange={(e) => onChangeValue('topLevelType', e || 0)}
            label="Top level type"
            tooltip="Top level type"
            disabled={
              !get(
                mapCol2,
                [value.fieldType, 'topLevelType', 'isEnabled'],
                true
              )
            }
            options={objectTypesOptions}
            data-testid="property-top-level-type"
          />
          <TEInput.Select
            value={value.titleType || undefined}
            placeholder="Select"
            onChange={(e) => onChangeValue('titleType', e || 0)}
            label="Title type"
            tooltip="Title type"
            disabled={
              !get(mapCol2, [value.fieldType, 'titleType', 'isEnabled'], true)
            }
            options={objectTypesOptions}
            data-testid="property-title-type"
          />
          <TEInput.Input
            value={value.refSeparator}
            onChange={(e) => onChangeValue('refSeparator', e)}
            label="Ref separator"
            tooltip="Ref separator"
            disabled={
              !get(
                mapCol2,
                [value.fieldType, 'refSeparator', 'isEnabled'],
                true
              )
            }
            data-testid="property-ref-separator"
          />
        </div>
        <div className="section-item">
          <TEInput.Switch
            checked={value.mandatory}
            onChange={(e) => {
              const implicitValues = calculateProperties({
                fieldType: value.fieldType,
                mandatory: e,
                multiple: value.multiple,
              });
              onChangeValue('mandatory', null, {
                ...implicitValues,
                mandatory: e,
              });
            }}
            label="Mandatory"
            tooltip="Mandatory"
            defaultChecked={
              mapCol2[value.fieldType as EFieldType]?.mandatory?.defaultValue
            }
            disabled={
              !get(mapCol2, [value.fieldType, 'mandatory', 'isEnabled'], true)
            }
            data-testid="property-mandatory"
          />
          <TEInput.Switch
            checked={value.unique}
            onChange={(e) => {
              // Unique has no effect on searchable, listable, primary, sortable
              onChangeValue('unique', e);
            }}
            label="Unique"
            tooltip="Unique"
            defaultChecked={
              mapCol2[value.fieldType as EFieldType]?.unique?.defaultValue
            }
            disabled={
              !get(mapCol2, [value.fieldType, 'unique', 'isEnabled'], true)
            }
            data-testid="property-unique"
          />
          <TEInput.Switch
            checked={value.multiple}
            onChange={(e) => {
              const implicitValues = calculateProperties({
                fieldType: value.fieldType,
                mandatory: value.mandatory,
                multiple: e,
              });
              onChangeValue('multiple', null, {
                ...implicitValues,
                multiple: e,
              });
            }}
            label="Multiple"
            tooltip="Multiple"
            defaultChecked={
              mapCol2[value.fieldType as EFieldType]?.multiple?.defaultValue
            }
            disabled={
              !get(mapCol2, [value.fieldType, 'multiple', 'isEnabled'], true)
            }
            data-testid="property-multiple"
          />
          <TEInput.Switch
            checked={value.editable}
            onChange={(e) => {
              // Editable has no effect on searchable, listable, primary, sortable
              onChangeValue('editable', e);
            }}
            label="Editable"
            tooltip="Editable"
            defaultChecked={
              mapCol2[value.fieldType as EFieldType]?.editable?.defaultValue
            }
            disabled={
              !get(mapCol2, [value.fieldType, 'editable', 'isEnabled'], true)
            }
            data-testid="property-editable"
          />
          {/* Maybe this should be moved to component package */}
          <div className="te-field-properties-table">
            <div className="te-field-property-col">
              <div className="te-field-property-head">
                <Typography.Text strong>Searchable</Typography.Text>
              </div>
              <div className="te-field-property-cell">
                {value.searchable ? 'Yes' : 'No'}
              </div>
            </div>
            <div className="te-field-property-col">
              <div className="te-field-property-head">
                <Typography.Text strong>Listable</Typography.Text>
              </div>
              <div className="te-field-property-cell">
                <Typography.Text>
                  {value.listable ? 'Yes' : 'No'}
                </Typography.Text>
              </div>
            </div>
            <div className="te-field-property-col">
              <div className="te-field-property-head">
                <Typography.Text strong>Primary</Typography.Text>
              </div>
              <div className="te-field-property-cell">
                <Typography.Text>
                  {value.primary ? 'Yes' : 'No'}
                </Typography.Text>
              </div>
            </div>
            <div className="te-field-property-col">
              <div className="te-field-property-head">
                <Typography.Text strong>Sortable</Typography.Text>
              </div>
              <div className="te-field-property-cell">
                {value.sortable ? 'Yes' : 'No'}
              </div>
            </div>
          </div>
        </div>
        <div className="section-item">
          <TEInput.Switch
            checked={value.searchableInReservationList}
            onChange={(e) => onChangeValue('searchableInReservationList', e)}
            label="Searchable in reservation"
            tooltip="Searchable in reservation"
            disabled={
              !get(
                mapCol2,
                [value.fieldType, 'searchableInReservationList', 'isEnabled'],
                true
              )
            }
            data-testid="resv-search"
          />
          <TEInput.Switch
            checked={value.reservationTextField}
            onChange={(e) => onChangeValue('reservationTextField', e)}
            label="Reservation text field"
            tooltip="Reservation text field"
            disabled={
              !get(
                mapCol2,
                [value.fieldType, 'reservationTextField', 'isEnabled'],
                true
              )
            }
            data-testid="resv-text-field"
          />
          {value.fieldType === EFieldType.CATEGORY && (
            <>
              <TEInput.Input
                value={value.virtualCategory}
                onChange={(e) => onChangeValue('virtualCategory', e)}
                label="Virtual category"
                tooltip="Virtual category"
                disabled={
                  !get(
                    mapCol2,
                    [value.fieldType, 'virtualCategory', 'isEnabled'],
                    true
                  )
                }
                data-testid="virtual-category"
              />
              <TEInput.Input
                value={value.abstractCategory}
                onChange={(e) => onChangeValue('abstractCategory', e)}
                label="Abstract category"
                tooltip="Abstract category"
                disabled={
                  !get(
                    mapCol2,
                    [value.fieldType, 'abstractCategory', 'isEnabled'],
                    true
                  )
                }
                data-testid="abstract-category"
              />
            </>
          )}
          {value.fieldType === EFieldType.INTEGER && (
            <>
              <TEInput.Switch
                checked={value.memberMaxCount}
                onChange={(e) => onChangeValue('memberMaxCount', e)}
                label="Member max count"
                tooltip="Member max count"
                data-testid="memberMaxCount"
              />
              <TEInput.Select
                value={value.memberMaxType || undefined}
                placeholder="Select"
                onChange={(e) => onChangeValue('memberMaxType', e || 0)}
                label="Member max type"
                options={objectTypesOptions}
                data-testid="memberMaxType"
              />
            </>
          )}
        </div>
      </div>
    </TEDrawerSection>
  );
};
